import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Instagram,
  KeyRound,
  RefreshCw,
  Unplug,
  CheckCircle2,
  AlertCircle,
  Upload,
  Trash2,
  Download,
  Film,
  PlayCircle,
  Copy,
  ShoppingCart,
  Zap,
  Gift,
  Bot,
  ExternalLink,
  Radar,
  Gamepad2
} from "lucide-react";
import { Button } from "./ui/button";
import { LearningPanel } from "./LearningPanel";

// Pestaña «Instagram» (solo admin): Reels con datos reales y temas del Radar, publicados en automático. Solo /api/admin/ig/*.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/ig`;

const authHeaders = () => {
  const token = localStorage.getItem("cortoviral_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const opts = () => ({ headers: authHeaders(), withCredentials: true });

const STATUS = {
  planning: { label: "Preparando", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  queued: { label: "En cola de render", style: "bg-slate-700/40 text-slate-300 border-white/10", busy: true },
  rendering: { label: "Montando", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  uploading: { label: "Enviando", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  processing: { label: "Instagram procesando", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  ready: { label: "Listo, sin publicar", style: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  published: { label: "En Instagram", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  failed: { label: "Fallido", style: "bg-red-500/15 text-red-300 border-red-500/30" }
};
const SOURCE_ICON = { radar: Radar, comparacesta: ShoppingCart, prisa: Zap, juegos: Gamepad2, elfo: Gift };

export const InstagramView = () => {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pasted, setPasted] = useState("");
  const [working, setWorking] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [preview, setPreview] = useState(null);
  const timer = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/account`, opts());
      setStatus(res.data);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "No se pudo cargar la conexión con Instagram." });
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/config`, opts());
      setConfig(res.data);
    } catch (e) {
      /* se reintenta en el siguiente refresco */
    }
  }, []);

  const loadVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/videos`, opts());
      setVideos(res.data.videos);
      setBusy(res.data.busy);
    } catch (e) {
      /* se reintenta en el siguiente refresco */
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadConfig();
    loadVideos();
  }, [loadStatus, loadConfig, loadVideos]);

  const anyBusy = busy || videos.some((v) => STATUS[v.status]?.busy);
  useEffect(() => {
    clearInterval(timer.current);
    if (anyBusy) timer.current = setInterval(() => { loadVideos(); loadStatus(); }, 5000);
    return () => clearInterval(timer.current);
  }, [anyBusy, loadVideos, loadStatus]);

  const run = async (key, fn, okText) => {
    setWorking(key);
    setFeedback(null);
    try {
      const msg = await fn();
      if (okText || msg) setFeedback({ type: "success", text: msg || okText });
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Ha habido un error." });
    } finally {
      setWorking((w) => (w === key ? null : w));
    }
  };

  const saveToken = () => run("token", async () => {
    await axios.post(`${API}/account/token`, { access_token: pasted }, opts());
    setPasted("");
    await loadStatus();
  }, "Cuenta de Instagram conectada. El token se renueva solo.");

  const disconnect = () => {
    if (!window.confirm("¿Desconectar la cuenta de Instagram? Se borra el token de este servidor.")) return;
    run("disconnect", async () => {
      await axios.post(`${API}/account/disconnect`, {}, opts());
      await loadStatus();
    }, "Cuenta de Instagram desconectada.");
  };

  const saveConfig = (patch) => run("config", async () => {
    const res = await axios.post(`${API}/config`, patch, opts());
    setConfig(res.data);
  });

  const refreshStats = () => run("stats", async () => {
    const res = await axios.post(`${API}/learning/refresh`, {}, opts());
    await loadConfig();
    const r = res.data;
    return `Estadísticas leídas: ${r.updated} Reels` + (r.no_views ? ` (${r.no_views} sin visitas: revisa el permiso de estadísticas)` : "") + ".";
  });

  const toggleIn = (list, item) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

  const createVideo = (source, mode) => run(`create-${source}-${mode || ""}`, async () => {
    await axios.post(`${API}/videos/create`, { source, mode, publish: !!status?.connected }, opts());
    setBusy(true);
    loadVideos();
  }, status?.connected
    ? "Creando el Reel. Al terminar se enviará a Instagram."
    : "Creando el vídeo. Como Instagram no está conectado, se quedará aquí para verlo y descargarlo.");

  const publishVideo = (v) => run(`pub-${v.id}`, async () => {
    await axios.post(`${API}/videos/${v.id}/publish`, {}, opts());
    loadVideos();
    loadStatus();
  }, "Enviado a Instagram. Lo procesa en unos minutos y se publica solo.");

  const deleteVideo = (v) => {
    if (!window.confirm(`¿Borrar «${v.title}» de CortoViral? (Si ya está en Instagram, allí se queda.)`)) return;
    run(`del-${v.id}`, async () => {
      await axios.delete(`${API}/videos/${v.id}`, opts());
      if (preview === v.id) setPreview(null);
      loadVideos();
    });
  };

  if (!status || !config) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-sm gap-2">
        <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Cargando Instagram...</span>
        {feedback && <span className="text-red-400 text-xs">{feedback.text}</span>}
      </div>
    );
  }

  const s = config.settings;
  const fbStyle = feedback?.type === "success"
    ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
    : "bg-red-500/10 border border-red-500/30 text-red-300";
  const card = "bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-3";
  const input = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-fuchsia-500/60";
  const chip = (on) => `px-2.5 py-1.5 rounded-lg text-xs font-semibold border flex items-center gap-1.5 transition-all ${
    on ? "bg-fuchsia-600/20 border-fuchsia-500/50 text-fuchsia-200" : "bg-black/30 border-white/10 text-slate-400 hover:text-white"}`;

  return (
    <div data-testid="ig-view" className="space-y-6">
      {/* Conexión */}
      <div className="bg-gradient-to-br from-[#12141D] to-[#2A0F2A] border border-fuchsia-500/30 rounded-2xl p-6 shadow-2xl space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {status.connected && status.avatar_url ? (
              <img src={status.avatar_url} alt="" className="w-12 h-12 rounded-full border border-fuchsia-500/40 shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-300 flex items-center justify-center shrink-0">
                <Instagram className="w-6 h-6" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black text-white">Instagram</h3>
                <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30">Solo admin</span>
                {status.connected ? (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                    Cuenta: @{status.username || "conectada"}
                  </span>
                ) : (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">Sin conectar</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">
                Reels con temas del Radar y datos reales de ComparaCesta, Prisa o Ahorro, tus juegos y Elfo Travieso.
                Va aparte de YouTube y Facebook y no gasta créditos.
              </p>
            </div>
          </div>
          {status.connected && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-slate-400 font-mono">Reels en 24 h: {status.posts_today}/{status.max_posts}</span>
              {status.followers != null && <span className="text-[11px] text-slate-400 font-mono">Seguidores: {status.followers}</span>}
              <Button variant="ghost" size="sm" onClick={disconnect} disabled={working !== null}
                className="h-8 text-xs text-slate-400 hover:text-red-300 flex items-center gap-1">
                <Unplug className="w-3.5 h-3.5" /> Desconectar
              </Button>
            </div>
          )}
        </div>

        {feedback && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 break-words ${fbStyle}`}>
            {feedback.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span className="min-w-0">{feedback.text}</span>
          </div>
        )}
        {status.last_error && <p className="text-[11px] text-red-400 break-words">Último error: {status.last_error}</p>}
        {!status.public_https && <p className="text-[11px] text-amber-300">Para publicar, Instagram debe poder descargar el vídeo: falta APP_PUBLIC_URL con https.</p>}

        {!status.connected && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-300 space-y-2 min-w-0">
              <p className="font-bold text-white flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-fuchsia-300" /> Cómo conectar</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-400">
                <li>En la app de Instagram, pasa la cuenta a <strong className="text-slate-200">profesional</strong> y déjala pública.</li>
                <li>En <strong className="text-slate-200">developers.facebook.com</strong>, añade a tu app el producto Instagram.</li>
                <li>Abre <strong className="text-slate-200">API setup with Instagram login</strong>, añade tu cuenta y genera el token.</li>
                <li>Pégalo aquí. Es tuyo: no lo compartas con nadie, tampoco en un chat.</li>
              </ol>
            </div>
            <div className="space-y-3">
              <div className="flex gap-2">
                <input type="password" placeholder="Token de Instagram" value={pasted} autoComplete="new-password"
                  onChange={(e) => setPasted(e.target.value)} className={input} />
                <Button data-testid="ig-token-btn" onClick={saveToken} disabled={working !== null || !pasted}
                  className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-black text-xs flex items-center gap-1.5 shrink-0">
                  {working === "token" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                  Conectar
                </Button>
              </div>
              <p className="text-[10px] text-slate-500">Se guarda solo en el servidor, nunca vuelve al navegador y se renueva solo.</p>
              <p className="text-[11px] text-slate-500">Sin conectar también puedes crear vídeos: se quedan aquí para verlos y descargarlos.</p>
            </div>
          </div>
        )}
      </div>

      {/* Crear ahora */}
      <div className={card}>
        <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Film className="w-4 h-4 text-fuchsia-300" /> Crear un Reel ahora</h4>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => createVideo("radar")} disabled={working !== null || busy || !config.radar_ready}
            className="bg-violet-700 hover:bg-violet-600 text-white font-bold text-xs flex items-center gap-1.5">
            <Radar className="w-3.5 h-3.5" /> Radar: un tema ({config.radar_ready})
          </Button>
          <Button onClick={() => createVideo("prisa")} disabled={working !== null || busy}
            className="bg-orange-600 hover:bg-orange-500 text-white font-bold text-xs flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" /> Prisa o Ahorro: un chollo
          </Button>
          <Button onClick={() => createVideo("comparacesta", "producto")} disabled={working !== null || busy}
            className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-xs flex items-center gap-1.5">
            <ShoppingCart className="w-3.5 h-3.5" /> ComparaCesta: un producto
          </Button>
          <Button onClick={() => createVideo("comparacesta", "cesta")} disabled={working !== null || busy}
            className="bg-emerald-800 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5">
            <ShoppingCart className="w-3.5 h-3.5" /> ComparaCesta: una cesta
          </Button>
          <Button onClick={() => createVideo("juegos")} disabled={working !== null || busy || !config.games_ready}
            className="bg-sky-700 hover:bg-sky-600 text-white font-bold text-xs flex items-center gap-1.5">
            <Gamepad2 className="w-3.5 h-3.5" /> Juego: el siguiente
          </Button>
          <Button onClick={() => createVideo("elfo")} disabled={working !== null || busy}
            className="bg-red-700 hover:bg-red-600 text-white font-bold text-xs flex items-center gap-1.5">
            <Gift className="w-3.5 h-3.5" /> Elfo: una travesura
          </Button>
        </div>
        <p className="text-[10px] text-slate-500">
          Se hace uno a la vez. En un Reel los enlaces no se pueden pulsar: pon los tuyos en la biografía de la cuenta.{busy && " Ahora mismo se está creando uno."}
        </p>
      </div>

      {/* Piloto */}
      <div className={card}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Bot className="w-4 h-4 text-fuchsia-300" /> Piloto automático</h4>
          <button data-testid="ig-autopilot-btn" onClick={() => saveConfig({ autopilot_enabled: !s.autopilot_enabled })} disabled={working !== null}
            className={`px-3 py-1.5 rounded-lg text-xs font-black border ${s.autopilot_enabled
              ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300" : "bg-slate-800 border-white/10 text-slate-400"}`}>
            {s.autopilot_enabled ? "ENCENDIDO" : "APAGADO"}
          </button>
        </div>
        <p className="text-xs text-slate-400">
          Crea y publica Reels solos, turnándose entre las fuentes activadas. Elfo Travieso solo entra de octubre a Nochebuena.
          {s.autopilot_enabled && config.next_autopilot && <> Próximo: <strong className="text-slate-200">{new Date(config.next_autopilot).toLocaleString("es-ES")}</strong>.</>}
        </p>
        {s.last_error && <p className="text-[11px] text-red-400 break-words">{s.last_error}</p>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-xs">
          <div className="space-y-2">
            <p className="text-slate-300 font-bold">Reels al día</p>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].filter((n) => n <= config.max_posts).map((n) => (
                <button key={n} onClick={() => saveConfig({ posts_per_day: n })} disabled={working !== null} className={chip(s.posts_per_day === n)}>{n}</button>
              ))}
            </div>
            <p className="text-slate-300 font-bold pt-1">Fuentes</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(config.source_names).map(([k, label]) => {
                const Icon = SOURCE_ICON[k] || Film;
                return (
                  <button key={k} onClick={() => saveConfig({ sources: toggleIn(s.sources || [], k) })} disabled={working !== null} className={chip((s.sources || []).includes(k))}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-slate-300">
              <input type="checkbox" checked={!!s.auto_publish} onChange={(e) => saveConfig({ auto_publish: e.target.checked })} className="accent-fuchsia-500 w-4 h-4" />
              Publicar en Instagram al terminar
            </label>
            <label className="flex items-center gap-2 text-slate-300">
              <input type="checkbox" checked={!!s.voice_enabled} onChange={(e) => saveConfig({ voice_enabled: e.target.checked })} className="accent-fuchsia-500 w-4 h-4" />
              Voz en off en todos los vídeos
            </label>
            <label className="block text-slate-300 font-bold pt-1">Código postal de ComparaCesta (opcional)</label>
            <input type="text" inputMode="numeric" maxLength={5} placeholder="Sin código: precios online generales" defaultValue={s.cc_postal_code || ""}
              onBlur={(e) => e.target.value !== (s.cc_postal_code || "") && saveConfig({ cc_postal_code: e.target.value })} className={input} />
          </div>
        </div>
      </div>

      {/* Aprendizaje del Piloto */}
      <LearningPanel
        testId="ig-learning"
        learn={config.learning}
        enabled={s.learning_enabled !== false}
        onToggle={() => saveConfig({ learning_enabled: s.learning_enabled === false })}
        onRefresh={refreshStats}
        refreshing={working === "stats"}
        saving={working !== null}
        lastStats={s.last_stats}
        lastError={s.last_stats_error}
        unit="Reels"
        unitOne="Reel"
        statsNote="cada 6 h, con la API de Instagram"
        accent="pink"
        groupTitle="Fuentes (multiplicador)"
        extra={{ title: "Categorías del Radar (multiplicador)", groups: config.learning?.radar_groups }}
        onText="Con 3 o más Reels medidos, el Piloto elige más a menudo las fuentes que mejor funcionan (todas siguen saliendo) y, en el Radar, prioriza las categorías que más visitas te dan."
        offText="Aprendizaje apagado: el Piloto va rotando las fuentes por orden y coge el tema del Radar con más puntuación."
        privateText={(n) => `${n === 1 ? "1 Reel no tiene" : `${n} Reels no tienen`} visitas: si pasa con todos, al token le falta el permiso instagram_business_manage_insights (genera uno nuevo con ese permiso marcado y pégalo arriba).`}
        favText="fuentes que salen más a menudo en el Piloto."
      />

      {/* Lista */}
      <div className={card}>
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Instagram className="w-4 h-4 text-fuchsia-300" /> Reels ({videos.length})</h4>
          <Button variant="ghost" size="sm" onClick={() => { loadVideos(); loadStatus(); }} className="h-8 text-xs text-slate-400 hover:text-white flex items-center gap-1">
            <RefreshCw className="w-3.5 h-3.5" /> Actualizar
          </Button>
        </div>
        {videos.length === 0 && <p className="text-xs text-slate-500">Todavía no hay ninguno.</p>}
        <div className="space-y-2">
          {videos.map((v) => {
            const st = STATUS[v.status] || STATUS.failed;
            const Icon = SOURCE_ICON[v.source] || Film;
            return (
              <div key={v.id} data-testid="ig-video-row" className="p-3 rounded-xl bg-black/30 border border-white/5 space-y-2">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white flex items-center gap-1.5 min-w-0">
                      <Icon className="w-3.5 h-3.5 text-fuchsia-300 shrink-0" /><span className="truncate">{v.title}</span>
                    </p>
                    <p className="text-[11px] text-slate-500 break-words">{v.progress}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${st.style}`}>{st.label}</span>
                    {v.video_url && !v.video_deleted && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setPreview(preview === v.id ? null : v.id)} className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                          <PlayCircle className="w-3.5 h-3.5" /> Ver
                        </Button>
                        <a href={`${BACKEND_URL}${v.video_url}`} onClick={async (e) => {
                          e.preventDefault();
                          const res = await axios.get(`${BACKEND_URL}${v.video_url}`, { ...opts(), responseType: "blob" });
                          const a = document.createElement("a");
                          a.href = URL.createObjectURL(res.data);
                          a.download = `instagram-${v.id.slice(0, 8)}.mp4`;
                          a.click();
                        }} className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                          <Download className="w-3.5 h-3.5" /> Bajar
                        </a>
                      </>
                    )}
                    {v.status === "ready" && status.connected && (
                      <Button size="sm" onClick={() => publishVideo(v)} disabled={working !== null}
                        className="h-7 px-2 bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-[11px] font-bold flex items-center gap-1">
                        <Upload className="w-3.5 h-3.5" /> Publicar
                      </Button>
                    )}
                    {v.post_url && (
                      <a href={v.post_url} target="_blank" rel="noopener noreferrer" className="h-7 px-2 text-[11px] text-emerald-300 hover:text-white flex items-center gap-1">
                        <ExternalLink className="w-3.5 h-3.5" /> Abrir
                      </a>
                    )}
                    {!st.busy && (
                      <Button size="sm" variant="ghost" onClick={() => deleteVideo(v)} disabled={working !== null} className="h-7 px-2 text-slate-500 hover:text-red-300">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {v.error && v.status !== "failed" && <p className="text-[11px] text-amber-300 break-words">{v.error}</p>}
                {preview === v.id && <ReelPreview video={v} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// El vídeo va con token en la cabecera, así que se baja como blob para enseñarlo
const ReelPreview = ({ video }) => {
  const [src, setSrc] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    let url;
    axios.get(`${BACKEND_URL}${video.video_url}`, { ...opts(), responseType: "blob" }).then((res) => {
      url = URL.createObjectURL(res.data);
      setSrc(url);
    }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [video.video_url]);
  return (
    <div className="flex flex-col sm:flex-row gap-3">
      {src ? <video src={src} controls className="w-40 rounded-lg border border-white/10 shrink-0" /> : <p className="text-xs text-slate-500">Cargando vídeo…</p>}
      <div className="text-xs text-slate-300 min-w-0 space-y-1">
        <p className="whitespace-pre-wrap break-words text-slate-400">{video.post?.caption}</p>
        {video.post?.tags?.length > 0 && <p data-testid="ig-tags" className="break-words text-fuchsia-300">{video.post.tags.join(" ")}</p>}
        {video.post?.caption && (
          <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(video.post.caption); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
            className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
            <Copy className="w-3 h-3" /> {copied ? "Copiado" : "Copiar texto"}
          </Button>
        )}
      </div>
    </div>
  );
};
